/** Shared by the selection page and its tabs — see `CompetitorSelectionPage`. */

import { DIALOG_ID_PARAM, DIALOG_PARAM } from "../../services/utils";

export const CONTRACTS_KEY = "competitor-contracts";
export const CANDIDATES_KEY = "competitor-candidates";
export const SELECTION_KEY = "competitor-selection";

/** 5 to 40 km in 5 km steps — mirrors `api/hebergements/radius.py.RADIUS_CHOICES`. */
export const RADIUS_CHOICES = [5, 10, 15, 20, 25, 30, 35, 40];

/**
 * One page of candidates. The map redraws per page rather than accumulating.
 *
 * ⚠️ 500 is the sweep's own ceiling (`search.MAX_PAGE_SIZE`), not a preference. A dense
 * radius holds more than that — 1 869 within 40 km of one Paris property — so
 * `COMPETITOR_SELECTION_PARTIAL` still reports "showing N of M" and means it. Raising
 * it further needs that ceiling lifted *and* a lighter payload for the map: every
 * candidate carries its full meta today, including two image URLs of ~200 characters,
 * and that is what makes a whole city expensive to send rather than the pin count.
 */
export const PAGE_SIZE = 500;

/**
 * The quality filters, and what they start at.
 *
 * `interest_rating` and `stars` start at *everything*, so neither filters until
 * someone touches it — `stars` unknown included. `interest_rating` 0 ("irrelevant")
 * is not an option at all, so it stays hidden either way. `rating` starts at 4-5.
 *
 * ⚠️ `stars` and `rating` carry an explicit unknown option because most of the
 * catalogue has neither — 63% no stars, 48% no review score. Without a box for it,
 * unchecking a single value would also drop every unrated competitor, which looks
 * like a bug and is impossible to guess at. `interest_rating` needs none: its column
 * is not nullable and the derivation falls back to 3.
 */
export const INTEREST_OPTIONS = [5, 4, 3, 2, 1];
export const STAR_OPTIONS = [5, 4, 3, 2, 1];
export const RATING_OPTIONS = [5, 4, 3, 2, 1];

export const DEFAULT_INTEREST_RATINGS = [...INTEREST_OPTIONS];
export const DEFAULT_STARS: (number | null)[] = [...STAR_OPTIONS, null];
export const DEFAULT_RATINGS: (number | null)[] = [4, 5];

/**
 * Deep link to a property's own configuration row, with its edit dialog open.
 *
 * ⚠️ No helper exists for this shape. `DIALOG_PARAM` / `DIALOG_ID_PARAM` are shared
 * (`services/utils`) and reused here, but the table tag is written literally the same
 * way `useConfigTableFiltersUrl` writes it — so if that page's tag is ever renamed,
 * this link and that hook both need it and neither will fail loudly.
 *
 * ⚠️ An absolute href, not a `RouterLink`: it opens in a new tab, because the
 * customer is mid-selection and losing the page to fix a coordinate is the wrong
 * trade.
 */
export const SURQUAL_PROPERTY_TABLE = "surqual_property";

export const propertyConfigUrl = (propertyId: number) => {
  const params = new URLSearchParams({
    _table: SURQUAL_PROPERTY_TABLE,
    [DIALOG_PARAM]: "edit",
    [DIALOG_ID_PARAM]: String(propertyId),
  });
  return `/configuration/overqualified/hebergement?${params.toString()}`;
};
